// ═══════════════════════════════════════════════════════════════════════════
// AdminReportsPanel — content-moderation queue (Admin → Reports tab).
//
// Visible to admins + managers with the 'moderation' scope. Lists
// user-submitted (and future auto-flagged) reports, filterable by
// status, with resolve actions: dismiss, mark reviewing, or action
// (with a free-text note of what was done).
// ═══════════════════════════════════════════════════════════════════════════

import React, { useCallback, useEffect, useState } from 'react';
import { listContentReports, resolveContentReport, REPORT_REASONS } from '../lib/moderation';
import { useToast } from '../contexts/ToastContext';
import Spinner from '../components/shared/Spinner';

const REASON_LABEL = Object.fromEntries(REPORT_REASONS.map(r => [r.id, r.label]));

const STATUS_TABS = [
  { id: 'open',      label: 'Open' },
  { id: 'reviewing', label: 'Reviewing' },
  { id: 'actioned',  label: 'Actioned' },
  { id: 'dismissed', label: 'Dismissed' },
  { id: null,        label: 'All' },
];

function timeAgo(iso) {
  if (!iso) return '';
  const d = Math.floor((Date.now() - new Date(iso).getTime()) / 86400000);
  const h = Math.floor((Date.now() - new Date(iso).getTime()) / 3600000);
  if (h < 1) return 'just now';
  if (h < 24) return `${h}h ago`;
  if (d < 7) return `${d}d ago`;
  return new Date(iso).toLocaleDateString(undefined, { month: 'short', day: 'numeric' });
}

export default function AdminReportsPanel() {
  const toast = useToast();
  const [statusFilter, setStatusFilter] = useState('open');
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [busyId, setBusyId] = useState(null);

  const reload = useCallback(async () => {
    setLoading(true);
    try { setRows(await listContentReports({ status: statusFilter })); }
    finally { setLoading(false); }
  }, [statusFilter]);
  useEffect(() => { reload(); }, [reload]);

  async function act(report, status, action) {
    setBusyId(report.id);
    try {
      await resolveContentReport(report.id, { status, action });
      toast.success(status === 'dismissed' ? 'Report dismissed' : `Marked ${status}`);
      reload();
    } catch (e) {
      toast.error(e?.message || 'Action failed');
    } finally {
      setBusyId(null);
    }
  }

  return (
    <div className="admin-reports">
      <h2 className="admin-reports-title">Content reports</h2>
      <p className="admin-reports-intro">
        User-submitted reports of content that may break the Community Guidelines.
        Review, then dismiss or action (remove content / warn / suspend).
      </p>

      <div className="admin-reports-tabs">
        {STATUS_TABS.map((t) => (
          <button
            key={t.label}
            className={`admin-reports-tab${statusFilter === t.id ? ' active' : ''}`}
            onClick={() => setStatusFilter(t.id)}
          >
            {t.label}
          </button>
        ))}
      </div>

      {loading ? (
        <div className="admin-reports-empty"><Spinner center /></div>
      ) : rows.length === 0 ? (
        <div className="admin-reports-empty">Nothing here — queue is clear.</div>
      ) : (
        <div className="admin-reports-list">
          {rows.map((r) => (
            <div key={r.id} className="admin-reports-row">
              <div className="admin-reports-row-main">
                <div className="admin-reports-row-head">
                  <span className={`admin-reports-reason admin-reports-reason--${r.reason}`}>
                    {REASON_LABEL[r.reason] || r.reason}
                  </span>
                  <span className="admin-reports-type">{r.content_type}</span>
                  {r.source === 'auto' && <span className="admin-reports-auto">auto-flagged</span>}
                  <span className="admin-reports-time">{timeAgo(r.created_at)}</span>
                </div>
                {r.details && <div className="admin-reports-details">"{r.details}"</div>}
                <div className="admin-reports-meta">
                  <span>Reported by {r.reporter_name || r.reporter_email || 'someone'}</span>
                  {(r.owner_name || r.owner_username) && (
                    <span> · owner: {r.owner_name || `@${r.owner_username}`}</span>
                  )}
                  <span> · id: {r.content_id?.slice(0, 12)}</span>
                </div>
                {r.status !== 'open' && r.status !== 'reviewing' && (
                  <div className="admin-reports-resolved">
                    {r.status === 'actioned' ? '✓ Actioned' : '✕ Dismissed'}
                    {r.action_taken ? ` — ${r.action_taken}` : ''}
                  </div>
                )}
              </div>

              {(r.status === 'open' || r.status === 'reviewing') && (
                <div className="admin-reports-actions">
                  {r.status === 'open' && (
                    <button className="admin-reports-btn" disabled={busyId === r.id}
                      onClick={() => act(r, 'reviewing')}>Reviewing</button>
                  )}
                  <button className="admin-reports-btn admin-reports-btn--danger" disabled={busyId === r.id}
                    onClick={() => act(r, 'actioned', window.prompt('What action did you take? (e.g. content removed, account suspended)') || 'actioned')}>
                    Action
                  </button>
                  <button className="admin-reports-btn admin-reports-btn--ghost" disabled={busyId === r.id}
                    onClick={() => act(r, 'dismissed', 'no violation')}>
                    Dismiss
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
